// Analytics API client.
//
// trackPageView is fire-and-forget — swallows all errors so broken analytics
// never interrupts navigation. getPageViews / getAnalyticsSummary are admin-
// only and throw on failure like every other authenticated request.

import { request } from './client'

export interface PageView {
  id: number
  user_id: number | null
  user_email: string | null
  path: string
  referrer: string | null
  ip: string | null
  user_agent: string | null
  created_at: string
}

export interface PathStat {
  path: string
  views: number
}

export interface UserStat {
  user_id: number
  email: string
  views: number
}

export interface RecentVisitor {
  user_id: number
  email: string
  last_seen: string
  view_count_7d: number
}

export interface AnalyticsSummary {
  total_views_24h: number
  total_views_7d: number
  unique_users_24h: number
  unique_users_7d: number
  unique_ips_24h: number
  top_paths_7d: PathStat[]
  top_users_7d: UserStat[]
  recent_visitors: RecentVisitor[]
}

export interface PageViewParams {
  limit?: number
  offset?: number
  user_id?: number
  path?: string
  since?: string
}

/** Fire-and-forget page view tracker. Never throws — errors are silently swallowed. */
export function trackPageView(path: string, referrer: string | null): void {
  fetch('/api/analytics/pageview', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ path, referrer }),
  }).catch(() => {
    // Intentionally silent — analytics down must not break navigation.
  })
}

export const analytics = {
  /** List raw page views. Admin only. */
  getPageViews: (params: PageViewParams = {}) => {
    const qs = new URLSearchParams()
    if (params.limit != null) qs.set('limit', String(params.limit))
    if (params.offset != null) qs.set('offset', String(params.offset))
    if (params.user_id != null) qs.set('user_id', String(params.user_id))
    if (params.path) qs.set('path', params.path)
    if (params.since) qs.set('since', params.since)
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return request<PageView[]>(`/analytics/pageviews${suffix}`)
  },

  /** Aggregated summary. Admin only. */
  getSummary: () => request<AnalyticsSummary>('/analytics/summary'),

  /** Per-user page view history (last N). Admin only. */
  getUserViews: (userId: number, limit = 50) =>
    request<PageView[]>(`/analytics/pageviews?user_id=${userId}&limit=${limit}`),
}
